import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { ForwardedRef, forwardRef } from 'preact/compat';
import { useEffect, useImperativeHandle, useRef, useState } from 'preact/hooks';
import { Permission } from 'zl-shared/dist/auth';

import { Video, VideoV2 } from '@/queries/media/types';

import AnimalIcon from '../AnimalIcon';
import { hasPermission } from '../Authorize';
import { PrimaryButton } from '../Buttons';
import EditVideoModal from '../EditVideoModal';
import { useIsMobileLayout } from '../LayoutDetector/hooks';
import Loader from '../Loader';
import MediaScheduleTag from '../MediaCollectionView/MediaScheduleTag';
import MobileMediaCard from '../MediaCollectionView/MobileMediaCard';
import RoundButton from '../RoundButton';
import { useVideoList } from './hooks';

interface HighlightsVideoListProps {
  type: 'curated' | 'clip';
  className?: string;
  play?: boolean;
  formulaHighlightOverride?: number;
  onVideoChange?: (highlight: VideoV2 | Video, index: number) => void;
  onVideoClick?: (highlight: VideoV2 | Video, index: number) => void;
  onLoadMore?: () => void;
  onUpgradeClick?: () => void;
  pageSize?: number;
}

export interface HighlightsVideoListRef {
  currentIndex: () => number | undefined;
  requestPlayVideoAtIndex: (index: number) => void;
  next: () => void;
  prev: (() => void) | undefined;
  stop: () => void;
}

const HighlightsVideoList = (
  {
    type,
    className,
    onVideoChange,
    onVideoClick,
    onLoadMore,
    onUpgradeClick,
    play,
    formulaHighlightOverride,
    pageSize,
  }: HighlightsVideoListProps,
  ref: ForwardedRef<HighlightsVideoListRef>,
) => {
  const isMobile = useIsMobileLayout();
  const canUploadMedia = hasPermission(Permission.Habitat.UploadMedia);
  const listRef = useRef<HTMLUListElement>(null);
  const [videoIndex, setVideoIndex] = useState<number | undefined>();

  const { data, isFetchingNextPage, hasNextPage, fetchNextPage, fetchMoreIfNeeded } = useVideoList(
    type,
    formulaHighlightOverride,
    pageSize,
  );
  const highlights = data?.list;

  const requestPlayVideoAtIndex = (index: number) => {
    if (!data) return;
    const video = highlights?.[index];
    fetchMoreIfNeeded(index);
    if (!video) {
      return;
    }
    setVideoIndex(index);
    onVideoChange?.(video, index);
    if (isMobile) {
      listRef.current?.querySelector(`[data-id="${video._id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  useImperativeHandle(ref, () => ({
    currentIndex: () => videoIndex,
    requestPlayVideoAtIndex,
    stop: () => {
      setVideoIndex(undefined);
    },
    next: () => {
      if (!data || videoIndex == null) {
        return;
      }
      requestPlayVideoAtIndex((videoIndex + 1) % data.list.length);
    },
    prev:
      data && videoIndex != null && videoIndex - 1 > 0
        ? () => {
            requestPlayVideoAtIndex((videoIndex - 1 + data.list.length) % data.list.length);
          }
        : undefined,
  }));

  const videoChangeHandler = (video: VideoV2, index: number) => {
    if (!data) return;
    fetchMoreIfNeeded(index);
    setVideoIndex(index);
    onVideoChange?.(video, index);
  };

  const loadMoreHandler = () => {
    fetchNextPage();
    onLoadMore?.();
  };

  // Play the first video when the list is requested to play when data is loaded
  const isPlayable = (highlights?.length ?? 0) > (videoIndex ?? 0);
  const isPlaying = videoIndex != null;
  const requestPlayVideoAtIndexRef = useRef(requestPlayVideoAtIndex);
  requestPlayVideoAtIndexRef.current = requestPlayVideoAtIndex; // We change the ref here to avoid the useEffect from being called on every render
  useEffect(() => {
    if (play && isPlayable && !isPlaying) {
      requestPlayVideoAtIndexRef.current?.(0);
    }
  }, [play, isPlayable, isPlaying]);

  return (
    <div className={className}>
      <ul ref={listRef} className="preflight preflight-ul grid grid-cols-1 gap-6 desktop:grid-cols-3 desktop:gap-3">
        {highlights?.map((highlight, index) => (
          <li
            data-group="Highlight List Item"
            key={highlight._id}
            data-id={highlight._id}
            data-title={highlight.title}
            className="mobile:scroll-m-6"
          >
            <a
              data-native
              href={`/highlights?type=${type}&id=${highlight._id}`}
              aria-disabled={highlight.disabled}
              className="preflight preflight-a group relative block gap-4 aria-disabled:opacity-50 mobile:grid mobile:grid-cols-[150px_1fr]"
              onClick={(e) => {
                e.preventDefault();
                videoChangeHandler(highlight, index);
                onVideoClick?.(highlight, index);
              }}
            >
              {/* Desktop Image + Overlay (Hidden on Mobile) */}
              <div className="preflight relative hidden overflow-hidden rounded desktop:block desktop:rounded-lg">
                <img
                  className="preflight preflight-img aspect-video"
                  src={highlight.thumbnailURL}
                  alt={highlight.title}
                  loading="lazy"
                />
                {videoIndex === index && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/75">
                    <div className="flex h-6 items-end gap-0.5 desktop:h-10">
                      <span className="h-[60%] w-2 animate-soundbar bg-white desktop:w-4" />
                      <span className="h-[30%] w-2 animate-soundbar bg-white [animation-delay:-2.2s] desktop:w-4" />
                      <span className="h-[75%] w-2 animate-soundbar bg-white [animation-delay:-3.7s] desktop:w-4" />
                    </div>
                  </div>
                )}
                {/* Desktop hover overlay: User badge, username, time and description */}
                <div className="pointer-events-none absolute inset-0 hidden desktop:block">
                  {highlight.habitat?.title && (
                    <div className="absolute left-4 top-3 z-[5] flex h-[18px] items-center justify-center rounded-[3px] bg-white/50 px-2 opacity-0 backdrop-blur-[2px] transition-opacity group-hover:opacity-100">
                      <span className="block text-[10px] font-bold leading-none text-black">{highlight.habitat.title}</span>
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 translate-y-full transform bg-gradient-to-t from-[rgb(38,38,38)] from-[17%] to-100% px-4 pb-4 pt-6 text-white transition group-hover:translate-y-0 group-hover:opacity-100">
                    <div className="flex items-center gap-1.5">
                      {highlight.user?.profile && (
                        <AnimalIcon
                          animalIcon={highlight.user.profile.animalIcon}
                          color={highlight.user.profile.color}
                          width={24}
                        />
                      )}
                      <span className="text-[10px] font-medium text-white">
                        {highlight.user?.username} at {format(new Date(highlight.publishedAt), 'ha').toLowerCase()}{' '}
                        {format(new Date(highlight.publishedAt), 'MMM d')}
                      </span>
                    </div>
                    <p className="preflight preflight-p mt-1 line-clamp-2 text-[13px] font-bold leading-tight text-white">
                      {highlight.title}
                    </p>
                  </div>
                </div>
              </div>

              {/* Mobile Media Card (Hidden on Desktop) */}
              <div className="contents desktop:hidden">
                <MobileMediaCard video={highlight} isPlaying={videoIndex === index} />
              </div>

              {canUploadMedia && (
                <div className="pointer-events-none absolute right-2 top-2 z-[3] hidden flex-row-reverse gap-3 desktop:group-hover:flex md:right-4 md:top-4">
                  <EditVideoModal.Trigger
                    videoId={highlight._id}
                    defaultThumbnailURL={highlight.thumbnailURL}
                    defaultTitle={highlight.title}
                    defaultPublishedAt={highlight.publishedAt}
                    canEditPlaylists={!highlight.disabled}
                    render={(setIsOpen) => (
                      <RoundButton
                        width={24}
                        color="var(--blueDark)"
                        backgroundColor="var(--white)"
                        className="pointer-events-auto"
                        onClick={(e: MouseEvent) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsOpen(true);
                        }}
                      >
                        <FontAwesomeIcon icon={faPenToSquare} className="text-xs" />
                        <span className="sr-only">Edit</span>
                      </RoundButton>
                    )}
                  />
                </div>
              )}
              <MediaScheduleTag publishedAt={highlight.publishedAt} />
            </a>
          </li>
        ))}
      </ul>
      {isFetchingNextPage && <Loader color="var(--charcoal)" className="my-4" width="64px" height="64px" />}
      {hasNextPage &&
        !isFetchingNextPage &&
        (onUpgradeClick ? (
          <div className="my-4 flex flex-col items-center gap-[11px]">
            <PrimaryButton
              label="Upgrade for Unlimited Clips"
              size={isMobile ? 'medium' : 'large'}
              className="mobile:!text-[15px] mobile:!font-medium desktop:text-sm desktop:!font-normal"
              onClick={onUpgradeClick}
            />
            <p className="font-bold text-primary-green mobile:whitespace-nowrap mobile:text-xs mobile:leading-4 desktop:text-xs">
              Or come back tomorrow for new daily highlights!
            </p>
          </div>
        ) : (
          <div className="my-4 flex justify-center">
            <PrimaryButton label="Load More" size={isMobile ? 'medium' : 'large'} onClick={loadMoreHandler} />
          </div>
        ))}
    </div>
  );
};

export default forwardRef(HighlightsVideoList);
